import { type FC, useEffect, useRef, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { Separator } from "@/shared/components/ui/separator";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

type WillAnswer = "yes" | "no" | "not-sure";

const WILL_OPTIONS: { value: WillAnswer; label: string }[] = [
  { value: "yes", label: "Yes, there is a will" },
  { value: "no", label: "No, there's no will" },
  { value: "not-sure", label: "I'm not sure" },
];

interface LegalAuthorityWillQuestionStepProps {
  personFirstName: string;
  estateId: number;
  onBack: () => void;
  onContinue: (answer: WillAnswer) => void;
}

export const LegalAuthorityWillQuestionStep: FC<LegalAuthorityWillQuestionStepProps> = ({
  personFirstName,
  estateId,
  onBack,
  onContinue,
}) => {
  const personName = personFirstName || "your loved one";
  const { savedAnswers, saveAnswers } = useWillStepAnswers(estateId);
  const [selected, setSelected] = useState<WillAnswer | "">("");
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const hasRestored = useRef(false);

  useEffect(() => {
    if (!savedAnswers || hasRestored.current) return;
    hasRestored.current = true;
    if (savedAnswers["has-will"]) {
      setSelected(savedAnswers["has-will"]);
    }
  }, [savedAnswers]);

  const handleContinue = async () => {
    if (!selected) {
      setError("Please select an option");
      return;
    }
    setSaving(true);
    try {
      await saveAnswers({ "has-will": selected });
      onContinue(selected);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <LegalAuthorityStepLayout
      title={`Did ${personName} leave a will?`}
      description={`If there's a will, you can share it on the next step. It's the single biggest factor in which court process fits ${personName}'s estate.`}
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleContinue} disabled={saving}>
          Continue
        </Button>
      }
    >
      <Separator />

      <RadioGroup
        value={selected}
        onValueChange={(v) => {
          setSelected(v as WillAnswer);
          setError(undefined);
        }}
        className="flex flex-col gap-4"
      >
        {WILL_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            selected={selected === option.value}
            onClick={() => {
              setSelected(option.value);
              setError(undefined);
            }}
            variant="radio"
            value={option.value}
          />
        ))}
      </RadioGroup>
      <FieldError message={error} />
    </LegalAuthorityStepLayout>
  );
};
